import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { QuoteService } from '../../../quote.service';
import { PolicyService } from '../../../../policy/policy.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-equine-quote-overview',
  templateUrl: './equine-quote-overview.component.html',
  styleUrls: ['./equine-quote-overview.component.css']
})
export class EquineQuoteOverviewComponent implements OnChanges {
  private static readonly TRANSACTION_LABELS: Record<number, string> = {
    1: 'New Business',
    2: 'Renewal',
    3: 'MTA',
    4: 'Cancellation',
    5: 'Lapsed',
    6: 'NTU',
  };

  @Input() quoteDetails: any = {};
  @Input() staticData: any = null;
  @Input() isBroker = false;


  brokerName: string = '';
  statusLabel: string = '';
  mtaReasonLabel = '';
  mtaReasonOther = '';

  private static readonly REFERRED_STATUS_ID = 'f6d14624-6351-4d93-b27b-70fb8a06ec5d';
  private static readonly QUOTED_STATUS_ID = '80c0db90-ba5d-4387-b09a-4c008a664932';
  private static readonly DECLINED_STATUS_ID = 'dda8734c-d149-4336-b8cc-c2e205430752';
  private static readonly EXPIRED_STATUS_ID = 'f1a42f5b-652b-42fc-864b-3462d63078ab';
  private static readonly IN_PROGRESS_STATUS_ID = 'e216564f-552a-4e45-ae67-0a63b7074691';

  private brokers: any[] = [];

  constructor(
    private quoteService: QuoteService,
    private policyService: PolicyService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteDetails'] && this.quoteDetails?.id) {
      this.resolveStatus();
      this.loadMtaReason();
      if (this.quoteDetails.broker_id && !this.brokerName) {
        this.loadBrokers();
      }
    }
  }

  get showMtaReason(): boolean {
    const txType = Number(this.quoteDetails?.transaction_type);
    return txType === 3 || txType === 4;
  }

  private resolveStatus(): void {
    const statusId = this.quoteDetails?.insurance_status_id;
    if (statusId === EquineQuoteOverviewComponent.REFERRED_STATUS_ID) {
      this.statusLabel = 'Referred';
    } else if (statusId === EquineQuoteOverviewComponent.EXPIRED_STATUS_ID) {
      this.statusLabel = 'Expired';
    } else if (statusId === EquineQuoteOverviewComponent.IN_PROGRESS_STATUS_ID) {
      this.statusLabel = 'In Progress';
    } else if (statusId === EquineQuoteOverviewComponent.DECLINED_STATUS_ID) {
      this.statusLabel = 'Declined';
    } else {
      this.statusLabel = 'Quoted';
    }
  }

  private loadMtaReason(): void {
    this.mtaReasonLabel = '';
    this.mtaReasonOther = '';
    if (!this.showMtaReason) {
      return;
    }
    const otherText = (this.quoteDetails?.mta_reason_other || '').trim();
    this.policyService.getPolicyMTAReasons(environment.NEW_EQUINE_ID).subscribe(
      (response: any) => {
        const reason = (response?.result?.types || []).find(
          (r: any) => r.id === this.quoteDetails?.mta_reason_id
        );
        this.mtaReasonLabel = (reason?.reasons || '').trim();
        this.mtaReasonOther = this.mtaReasonLabel.toLowerCase() === 'other' ? otherText : '';
      }
    );
  }

  public getTransactionTypeLabel(): string {
    const txType = Number(this.quoteDetails?.transaction_type) || 1;
    return EquineQuoteOverviewComponent.TRANSACTION_LABELS[txType];
  }

  private loadBrokers(): void {
    this.quoteService.getBrokers(10).subscribe(
      (response: any) => {
        if (response.success && Array.isArray(response.result)) {
          this.brokers = response.result;
          this.resolveBrokerName();
        }
      }
    );
  }

  private resolveBrokerName(): void {
    const brokerId = this.quoteDetails?.broker_id || this.quoteDetails?.company_id;
    if (!brokerId) return;
    const broker = this.brokers.find((b: any) => b.id === brokerId);
    this.brokerName = broker?.name || '';
  }

  /** Resolve ownership status ID to display name from staticData. */
  getOwnershipStatusName(id: string | number | null | undefined): string {
    if (id == null || id === '') return '';
    const list = this.staticData?.ownership_statuses;
    if (!Array.isArray(list)) return '';
    const item = list.find((s: any) => s.id === id || String(s.id) === String(id));
    return item ? (item.status_name ?? '') : '';
  }

  /** Normalized address from proposer_details (handles address / address_id or nested shape). */
  get proposerAddress(): string {
    const pd = this.quoteDetails?.proposer_details;
    if (!pd) return '';
    const addr = pd.address;
    if (!addr || typeof addr !== 'object') return '';
    const a1 = addr.address1 ?? addr.address_1 ?? '';
    const a2 = addr.address2 ?? addr.address_2 ?? '';
    const city = addr.city ?? addr.town_county ?? '';
    const postcode = addr.postcode ?? '';
    const parts = [a1, a2, city, postcode].filter(Boolean);
    return parts.join(', ');
  }

  public getHorseAgeDisplay(): string {
    if (!this.quoteDetails.horse_details.age) return 'N/A';
    return this.quoteDetails.horse_details.age == '<1' ? '<1' : Number(this.quoteDetails.horse_details.age).toString();
  }
}
